import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AuthLayout } from '../layouts';
import { api } from '../lib/api';
import { tokenRefreshService } from '../lib/tokenRefresh';
import { sessionTimeoutService } from '../lib/sessionTimeout';

type LoginMethod = 'whatsapp' | 'email';
type WhatsAppStep = 'phone' | 'otp';

// Feature flag: Set to false to enable email login only
const ENABLE_WHATSAPP_LOGIN = false;

export const AdminLogin = () => {
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  // WhatsApp login state
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState<string>('');
  const [whatsappStep, setWhatsappStep] = useState<WhatsAppStep>('phone');

  // Email login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Request OTP mutation
  const requestOTPMutation = useMutation({
    mutationFn: (identifier: string) => api.requestOTP(identifier),
    onSuccess: (data) => {
      setSessionId(data.sessionId);
      setWhatsappStep('otp');
      setError('');
    },
    onError: (error: Error) => {
      setError(error.message || 'Failed to send OTP');
    }
  });

  // Verify OTP mutation
  const verifyOTPMutation = useMutation({
    mutationFn: ({ sessionId, otp }: { sessionId: string; otp: string }) => 
      api.verifyOTP(sessionId, otp),
    onSuccess: (data) => {
      handleSuccessfulLogin(data);
    },
    onError: (error: Error) => {
      setError(error.message || 'Failed to verify OTP');
    }
  });

  // Email/Password login mutation
  const emailLoginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.loginWithPassword(email, password),
    onSuccess: (data) => {
      handleSuccessfulLogin(data);
    },
    onError: (error: Error) => {
      setError(error.message || 'Invalid email or password');
    }
  });

  const handleSuccessfulLogin = (data: {
    token: string;
    refreshToken: string;
    expiresIn: number;
    user: any;
  }) => {
    // Check if user has admin privileges
    const allowedRoles = ['super_admin', 'admin', 'expert'];
    const hasAllowedRole = data.user.roles?.some((role: string) => allowedRoles.includes(role));
    
    if (!hasAllowedRole) {
      setError('Access denied. This portal is only for administrators, experts, and super admins.');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return;
    }

    // Store tokens and user data
    localStorage.setItem('token', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // Initialize token refresh service
    tokenRefreshService.initializeTokenRefresh(data.expiresIn);
    
    // Start session timeout tracking
    sessionTimeoutService.startSession(false);
    console.log('Session timeout tracking started');
    
    // Redirect based on user role
    if (data.user.roles?.includes('super_admin')) {
      navigate('/analytics');
    } else {
      navigate('/users');
    }
  };

  const handleSendOTP = () => {
    if (!phone.trim()) {
      setError('Please enter a valid phone number');
      return;
    }
    requestOTPMutation.mutate(phone.trim());
  };

  const handleVerifyOTP = () => {
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    if (!sessionId) {
      setError('Session expired. Please request a new OTP.');
      setWhatsappStep('phone');
      return;
    }
    verifyOTPMutation.mutate({ sessionId, otp: otp.trim() });
  };

  const handleEmailLogin = () => {
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    emailLoginMutation.mutate({ email: email.trim(), password });
  };

  const isLoading = requestOTPMutation.isPending || verifyOTPMutation.isPending || emailLoginMutation.isPending;

  return (
    <AuthLayout>
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Portal
          </h1>
          <p className="text-gray-600">Sign in to continue</p>
        </div>

        {/* Login Method Tabs */}
        {ENABLE_WHATSAPP_LOGIN && (
          <div className="flex mb-6 border-b border-gray-200">
            <button
              onClick={() => {
                setLoginMethod('email');
                setError('');
              }}
              className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                loginMethod === 'email'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Email & Password
            </button>
            <button
              onClick={() => {
                setLoginMethod('whatsapp');
                setError('');
              }}
              className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                loginMethod === 'whatsapp'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              WhatsApp OTP
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Email/Password Login Form */}
        {loginMethod === 'email' && (
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleEmailLogin()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                placeholder="admin@example.com"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleEmailLogin()}
                  onBlur={() => setShowPassword(false)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={handleEmailLogin}
              disabled={isLoading || !email.trim() || !password.trim()}
              className="w-full py-3 px-4 text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        )}

        {/* WhatsApp OTP Login Form */}
        {loginMethod === 'whatsapp' && ENABLE_WHATSAPP_LOGIN && (
          <div className="space-y-6">
            {whatsappStep === 'phone' ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendOTP()}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                    placeholder="+1234567890"
                    disabled={isLoading}
                    autoComplete="tel"
                  />
                </div>
                <button
                  onClick={handleSendOTP}
                  disabled={isLoading || !phone.trim()}
                  className="w-full py-3 px-4 text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter OTP
                  </label>
                  <input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleVerifyOTP()}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                    placeholder="Enter the OTP sent to your WhatsApp"
                    disabled={isLoading}
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setWhatsappStep('phone');
                      setOtp('');
                      setError('');
                    }}
                    disabled={isLoading}
                    className="flex-1 py-3 px-4 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleVerifyOTP}
                    disabled={isLoading || !otp.trim()}
                    className="flex-1 py-3 px-4 text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AuthLayout>
  );
};
